/* What did not match, during the shadow week.
 *
 * Ticket 5.2. Two audiences on one screen, because they are doing two halves
 * of the same job:
 *
 *   Anybody, including a courier: report one. The form is first and short,
 *   because it is filled in by somebody who has just noticed something and
 *   has a van running outside.
 *
 *   Staff: read the log and close things. Open and worst at the top, since
 *   that is the order they get dealt with in.
 *
 * The go-live panel deliberately does not show a green tick when the board is
 * clean. A clean board is necessary and not sufficient, a person decides, and
 * a tick would invite somebody to treat it as the decision.
 */

import { useCallback, useEffect, useState, type FormEvent } from 'react';
import type { Site } from './Sites';
import { Link, useParams } from 'react-router-dom';
import { api, why, type Discrepancy, type DiscrepancySummary } from '../../lib/api';
import { momentFor, todayIn } from '../../lib/when';
import { useAuth, useProjectTimezone } from '../../app/auth';
import { Loading } from '../../app/Loading';
import { Pager, usePaged } from '../../app/Pager';
import { Section } from '../../app/Section';
import { Context, Panel, Screen, ScreenHeader, Sheet } from '../../app/Glass';

const KINDS: Array<{ value: string; label: string }> = [
    { value: 'delivery', label: 'What was recorded at the door' },
    { value: 'import', label: 'The list, or what it created' },
    { value: 'assignment', label: 'Who the system says has it' },
    { value: 'timing', label: 'Times, or the SLA answer' },
    { value: 'billing', label: 'What it would be charged' },
    { value: 'system', label: 'The app itself' },
    { value: 'other', label: 'Something else' },
];

const SEVERITIES: Array<{ value: string; label: string; help: string }> = [
    { value: 'critical', label: 'Critical', help: 'A delivery record is wrong or missing. This stops go-live on its own.' },
    { value: 'major', label: 'Major', help: 'Wrong, but caught and put right within the day.' },
    { value: 'minor', label: 'Minor', help: 'Awkward, slow or confusing. Nothing was recorded wrongly.' },
];

export function Discrepancies() {
    const { code = '' } = useParams();
    const { projects } = useAuth();
    const project = projects.find((p) => p.code === code);
    /* The contract's day. From 7pm in Chicago, UTC is already tomorrow, and a
       courier filing something at the end of a round would date it to a day
       that has not happened. */
    const timezone = useProjectTimezone(code);
    const today = () => todayIn(timezone);
    const canReview = project !== undefined && project.role === 'admin';
    const base = `/api/projects/${code}/uh/discrepancies`;
    /* When a courier filed it, in the zone the day it is about was worked
       in. This is evidence for a go-live decision and it is read later. */
    const when = momentFor(timezone);

    /* ─────────────── WHICH DAY AND WHICH COUNTER THE LOG IS SHOWING
     *
     * The log had neither. It asked for `?status=open` and showed every
     * open dispute on the contract, which on nine pharmacies is a list
     * nobody can act on: the question somebody actually has is "what is
     * outstanding at Wheatley for Tuesday".
     *
     * The date box further up this page is NOT this. That one belongs to
     * the report form -- which day the dispute is about -- and reusing it
     * would mean filing a report changed what the list showed.
     *
     * EMPTY MEANS EVERY, for both. A log that defaulted to today would
     * hide an open dispute from last week, which is the one most worth
     * chasing. */
    const [filterDate, setFilterDate] = useState('');
    const [filterSite, setFilterSite] = useState<number | ''>('');
    const [sites, setSites] = useState<Site[]>([]);

    const [list, setList] = useState<Discrepancy[] | null>(null);
    const [summary, setSummary] = useState<DiscrepancySummary | null>(null);
    const [msg, setMsg] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
    const [busy, setBusy] = useState(false);

    const [serviceDate, setServiceDate] = useState(today());
    const [kind, setKind] = useState('delivery');
    const [severity, setSeverity] = useState('major');
    const [reference, setReference] = useState('');
    const [expected, setExpected] = useState('');
    const [actual, setActual] = useState('');
    /* Which row is being closed, and with what. Was window.prompt(), which is
       unstyled, unlabelled, cannot be validated, and is blocked outright in
       some browsers and embedded webviews: there the button threw and did
       nothing at all. Closing a discrepancy is the sentence somebody reads
       back at the end of the shadow week, so it deserves a real field.
       Found by walking docs/day-rehearsal.md. */
    const [closing, setClosing] = useState<{ id: number; status: 'resolved' | 'accepted' } | null>(null);
    const [resolution, setResolution] = useState('');

    const load = useCallback(async () => {
        if (!canReview) return;
        /* ASKED OF THE SERVER, not sifted here. The endpoint caps at 500
           rows, so a filter applied in the browser could never reach a
           dispute past that edge -- which is exactly the old one somebody
           is hunting for. */
        const q = new URLSearchParams({ status: 'open' });
        if (filterDate !== '') q.set('serviceDate', filterDate);
        if (filterSite !== '') q.set('siteId', String(filterSite));
        try {
            const [l, s] = await Promise.all([
                api<Discrepancy[]>(`${base}?${q.toString()}`),
                api<DiscrepancySummary>(`${base}/summary`),
            ]);
            setList(l);
            setSummary(s);
        } catch (err) {
            setMsg({ kind: 'error', text: why(err, 'Could not load the log') });
        }
    }, [base, canReview, filterDate, filterSite]);
    useEffect(() => { void load(); }, [load]);

    useEffect(() => {
        if (!canReview) return;
        /* Silent on failure: without the sites the pharmacy filter simply
           offers nothing, and the log still works. */
        void api<Site[]>(`/api/projects/${code}/uh/sites`).then(setSites).catch(() => setSites([]));
    }, [code, canReview]);

    const report = (e: FormEvent) => {
        e.preventDefault();
        setBusy(true);
        setMsg(null);
        void (async () => {
            try {
                /* A DELIVERY NUMBER THAT IS NOT A NUMBER USED TO BECOME
                   `null` ON THE WIRE. `Number('abc')` is NaN, JSON.stringify
                   writes NaN as null, and the schema answers "orderId:
                   Expected number, received null" -- the same shape of bug
                   as the photo upload on 31 October. A reference that is not
                   a number is now simply not sent, and the courier is told
                   rather than the server. */
                const ref = reference.trim();
                const orderId = ref === '' ? null : Number(ref);
                if (orderId !== null && !Number.isInteger(orderId)) {
                    setMsg({ kind: 'error', text: 'The delivery number has to be a number, like 4182. Leave it empty if this is not about one delivery.' });
                    return;
                }
                /* Trimmed here as well as on the server. `required` lets a
                   box of spaces through and `.trim().min(3)` then refuses
                   it, which is a refusal nobody can see the cause of. */
                await api(base, {
                    method: 'POST',
                    json: {
                        serviceDate, kind, severity, expected: expected.trim(), actual: actual.trim(),
                        ...(orderId === null ? {} : { orderId }),
                    },
                });
                setExpected(''); setActual(''); setReference('');
                setMsg({ kind: 'ok', text: 'Filed. Thank you: a week with no reports means nobody was looking.' });
                await load();
            } catch (err) {
                setMsg({ kind: 'error', text: why(err, 'Could not file it') });
            } finally {
                setBusy(false);
            }
        })();
    };

    const close = (e: FormEvent) => {
        e.preventDefault();
        if (!closing) return;
        const { id, status } = closing;
        void (async () => {
            try {
                await api(`${base}/${id}`, { method: 'PATCH', json: { status, resolution } });
                setClosing(null);
                setResolution('');
                await load();
            } catch (err) {
                setMsg({ kind: 'error', text: why(err, 'Could not close it') });
            }
        })();
    };

    const pagedDays = usePaged(summary?.days ?? []);
    const pagedOpen = usePaged(list ?? []);

    if (!project) return (<><h1>Project not available</h1><Link className="izy-btn secondary" to="/">Back to projects</Link></>);

    /* ONE SCREEN, TWO SHAPES, because it already had two jobs.
     *
     * A courier reaches this from "Something did not match" on their run,
     * on a phone, to file one thing they just saw. Dispatch reaches it at a
     * desk to read what was filed and decide what it means for go-live.
     * The form, the wording and the server call are the same for both; the
     * frame around them is not, and pretending otherwise is how the driver
     * side ended up looking like a console in the first place.
     *
     * So the body is built once and the frame is chosen by the same
     * `canReview` that already decides the fold and whether the summary is
     * even requested. */
    const page = (
        <>
            {msg && <div className={`izy-alert ${msg.kind}`} role={msg.kind === 'error' ? 'alert' : 'status'}>{msg.text}</div>}

            {/* One screen, two jobs. A courier reaches this from "Something
                not matching what you see?" on their run, to file one, so the
                form is open and costs them nothing. Dispatch reaches it to
                read what was filed, so for them it starts folded and the
                week's state is the first thing on the page. Same screen, and
                the default follows who is looking at it. */}
            <Section
                id="uh.discrepancies.report"
                title="Report one"
                defaultOpen={!canReview}
                summary="something the system and the day disagree about"
            >
            <form onSubmit={report}>
                <label className="izy-field">Which day it was about
                    <input type="date" value={serviceDate} onChange={(e) => setServiceDate(e.target.value)} required />
                </label>
                <label className="izy-field">What it is about
                    <select value={kind} onChange={(e) => setKind(e.target.value)}>
                        {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                    </select>
                </label>
                <label className="izy-field">How bad
                    <select value={severity} onChange={(e) => setSeverity(e.target.value)}>
                        {SEVERITIES.map((sv) => <option key={sv.value} value={sv.value}>{sv.label}</option>)}
                    </select>
                </label>
                <p className="izy-muted">{SEVERITIES.find((sv) => sv.value === severity)?.help}</p>

                <label className="izy-field">Delivery number, if it is about one
                    <input inputMode="numeric" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="4182" />
                </label>
                {/* Said on the screen, because somebody under time pressure
                    will otherwise identify a delivery the quick way. */}
                <p className="izy-muted">
                    Point at a delivery by its number. Please do not type a patient&apos;s name in the boxes below.
                </p>

                <label className="izy-field">What the system said
                    <textarea rows={2} value={expected} onChange={(e) => setExpected(e.target.value)} required minLength={3} />
                </label>
                <label className="izy-field">What actually happened
                    <textarea rows={2} value={actual} onChange={(e) => setActual(e.target.value)} required minLength={3} />
                </label>
                <button className="izy-btn" type="submit" disabled={busy}>{busy ? 'Filing' : 'File it'}</button>
            </form>
            </Section>

            {!canReview && (
                <p className="izy-muted">
                    Dispatch reviews these at the end of each day. You will hear what was found.
                </p>
            )}

            {canReview && summary && (
                <div className="izy-card">
                    <h2>Where the week stands</h2>
                    <div className="izy-stats">
                        <div className={summary.goLive.openCritical > 0 ? 'izy-stat-bad' : undefined}>
                            <b>{summary.goLive.openCritical}</b><span>critical, open</span>
                        </div>
                        <div><b>{summary.totals.open}</b><span>open</span></div>
                        <div><b>{summary.totals.resolved}</b><span>resolved</span></div>
                        <div><b>{summary.totals.accepted}</b><span>accepted</span></div>
                    </div>
                    {/* No green tick. A clean board is not a decision. */}
                    <p>{summary.goLive.why}</p>
                    {summary.days.length > 0 && (
                        <>
                        <table className="izy-table">
                            <thead><tr><th>Day</th><th>Open</th><th>Resolved</th><th>Accepted</th><th>Critical</th></tr></thead>
                            <tbody>
                                {pagedDays.rows.map((d) => (
                                    <tr key={d.serviceDate}>
                                        <td>{d.serviceDate}</td><td>{d.open}</td><td>{d.resolved}</td>
                                        <td>{d.accepted}</td><td>{d.critical}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                            <Pager of={pagedDays} noun="days" />
                        </>
                    )}
                </div>
            )}

            {canReview && (
                <div className="izy-card">
                    <h2>Still open</h2>
                    {/* THE FILTER SITS ON THE LIST IT NARROWS. A control a
                        scroll away from what it changes gets used by
                        nobody, and this page already has a date box
                        elsewhere that means something else entirely. */}
                    <div className="izy-row">
                        <label className="izy-field">Day
                            <input
                                type="date"
                                value={filterDate}
                                onChange={(e) => setFilterDate(e.target.value)}
                            />
                        </label>
                        <label className="izy-field">Pharmacy
                            <select
                                value={filterSite}
                                onChange={(e) => setFilterSite(e.target.value === '' ? '' : Number(e.target.value))}
                            >
                                <option value="">Every pharmacy</option>
                                {sites.map((st) => <option key={st.id} value={st.id}>{st.name}</option>)}
                            </select>
                        </label>
                        {(filterDate !== '' || filterSite !== '') && (
                            <button
                                type="button"
                                className="izy-btn secondary"
                                onClick={() => { setFilterDate(''); setFilterSite(''); }}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    {list === null ? <Loading label="Loading the log" /> : list.length === 0 ? (
                        /* WHICH EMPTY IT IS. "Nothing open" under a filter
                           that is hiding four disputes is the wrong thing
                           to tell somebody deciding whether to go live. */
                        <div className="izy-muted">
                            {filterDate === '' && filterSite === ''
                                ? 'Nothing open.'
                                : 'Nothing open for that day and pharmacy.'}
                        </div>
                    ) : (<>{pagedOpen.rows.map((d) => (
                        <div key={d.id} className="izy-pool-group">
                            <div>
                                {/* `off`, not `izy-stat-bad`. The latter is a stat tile modifier and
                                    is not one of izy-pill's tones, so a critical
                                    discrepancy was being drawn as red text on the
                                    green "everything is fine" background. */}
                                <span className={`izy-pill ${d.severity === 'critical' ? 'off' : d.severity === 'major' ? 'warn' : 'muted'}`}>{d.severity}</span>
                                {' '}<b>{d.kind}</b>{' · '}{d.serviceDate}
                                {d.reference && <>{' · '}{d.reference}</>}
                                <div className="izy-muted">
                                    {d.reportedBy}, {when(d.reportedAt)}
                                </div>
                            </div>
                            <p><b>System:</b> {d.expected}</p>
                            <p><b>Actually:</b> {d.actual}</p>
                            {closing?.id === d.id ? (
                                <form onSubmit={close}>
                                    <label className="izy-field">
                                        {closing.status === 'resolved'
                                            ? 'What was changed? Code, data, process, or what somebody was taught.'
                                            : 'Why does nothing need changing?'}
                                        <input
                                            value={resolution}
                                            onChange={(ev) => setResolution(ev.target.value)}
                                            required
                                            autoFocus
                                        />
                                    </label>
                                    <div className="izy-row">
                                        <button className="izy-btn small" type="submit" disabled={busy}>
                                            {closing.status === 'resolved' ? 'Close as changed' : 'Close as accepted'}
                                        </button>
                                        <button
                                            className="izy-btn secondary small"
                                            type="button"
                                            onClick={() => { setClosing(null); setResolution(''); }}
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                <div className="izy-row">
                                    <button
                                        className="izy-btn small"
                                        type="button"
                                        onClick={() => { setClosing({ id: d.id, status: 'resolved' }); setResolution(''); }}
                                    >
                                        Something was changed
                                    </button>
                                    <button
                                        className="izy-btn secondary small"
                                        type="button"
                                        onClick={() => { setClosing({ id: d.id, status: 'accepted' }); setResolution(''); }}
                                    >
                                        Nothing needs changing
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                    {/* Resolving one removes it from this list, so the page
                        can shrink under the reader. usePaged clamps rather
                        than leaving them on an empty page 3 of 1. */}
                    <Pager of={pagedOpen} noun="open discrepancies" />
                    </>)}
                </div>
            )}
        </>
    );

    if (!canReview) {
        return (
            <Screen>
                <Context>
                    <ScreenHeader title="Discrepancies" subtitle="Tell us what you saw" />
                    <Panel>
                        <h2 className="izy-lede-kicker">Something did not match</h2>
                        <p className="izy-lede-note">
                            Anything where this system and what actually happened disagree. When in
                            doubt, file it: a duplicate costs a minute, a missed one costs the
                            go-live decision.
                        </p>
                    </Panel>
                </Context>
                <Sheet>{page}</Sheet>
            </Screen>
        );
    }

    return (
        <>
            <h1>Discrepancies</h1>
            <p className="izy-sub">
                Anything where this system and what actually happened disagree. When in doubt, file it:
                a duplicate costs a minute, a missed one costs the go-live decision.
            </p>
            {page}
        </>
    );
}
